import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "../api/baseQuery";
import type { TClientForm } from "../types/index.type";

/** Why the server pre-selected a patient (see backend ``client.schedule_pick``). */
export type SchedulePickReason = "in_window" | "next_after_last_note" | "next_undocumented" | "first_of_day";
/** Why it did not. The drawer adds its own client-side reasons on top (see useSchedulePick). */
export type SchedulePickNoPickReason = "all_documented" | "no_schedule" | "stale";
export type SchedulePickState = "ready" | "empty" | "stale";

export interface ScheduleSlot {
  client: TClientForm;
  /** Appointment start, ISO instant. */
  scheduled_at: string;
  /** When this clinician's first real note for the patient began today, ISO instant; null if none. */
  documented_at: string | null;
}

export interface SchedulePickSuggestion {
  client_id: number;
  reason: SchedulePickReason;
  confidence: "high" | "low";
  /** Undocumented patients booked in the picked slot, the pick included. */
  tie_size: number;
  /** Minutes from the slot to "now"; positive means the slot has already started. */
  slot_offset_min: number;
}

export interface SchedulePick {
  state: SchedulePickState;
  clinic_tz: string;
  /** The clinic-local day the payload describes, YYYY-MM-DD. */
  day: string;
  /** When the day sheet was last applied by a pull, ISO instant; null if never stamped. */
  synced_at: string | null;
  suggestion: SchedulePickSuggestion | null;
  no_pick_reason: SchedulePickNoPickReason | null;
  /** Undocumented and booked around now, in pick order; the suggestion is the first row. */
  recommended: ScheduleSlot[];
  /** Still to be seen today, after the recommended group. */
  up_next: ScheduleSlot[];
  /** Already has a note from this clinician today; most recent slot first. */
  documented: ScheduleSlot[];
  candidates_in_window: number;
  schedule_size: number;
}

export type SchedulePickArgs = {
  /** Rank against the moment this recording began (record-first "Add Encounter Details"). */
  audio_id?: number | null;
};

export const schedulePickApi = createApi({
  reducerPath: "schedulePickApi",
  baseQuery: baseQueryWithAuth,
  // The sheet changes every time a note lands, so a cached answer is a stale answer:
  // every drawer open fetches afresh.
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getTodaySchedulePick: builder.query<SchedulePick, SchedulePickArgs>({
      query: ({ audio_id } = {}) => ({
        url: "/client/schedule/today/",
        params: audio_id != null ? { audio_id } : undefined,
      }),
    }),
  }),
});

export const { useLazyGetTodaySchedulePickQuery } = schedulePickApi;
